import React from 'react'
import { Box, Checkbox } from '@mui/material'
import { AGE, C, MONO, tnum } from './tokens'
import { ageLevel } from './model'
import { AgeBadge, RestaurantLogo, StatusBadge } from './Badges'
import { AmountCell, DeliveryCell, NoAddressChip } from './cells'
import { useDispatchUi } from './context'

/** Phone/tablet stand-in for OrderRow: the fields a dispatcher acts on, stacked. */
export default function OrderCard({ view, selected, onToggle }) {
  const ui = useDispatchUi()
  const level = ageLevel(view, ui.now)

  return (
    <Box
      component="article"
      tabIndex={0}
      onClick={() => ui.openOrder(view.id)}
      onKeyDown={e => {
        if (e.key === 'Enter' && e.target === e.currentTarget) ui.openOrder(view.id)
      }}
      sx={{
        position: 'relative', display: 'flex', gap: 1.25, py: 1.5, pl: 1.5, pr: 2, cursor: 'pointer', outline: 'none',
        bgcolor: selected ? 'rgba(238,244,255,0.7)' : C.card,
        '&:hover': { bgcolor: selected ? 'rgba(238,244,255,1)' : C.subtle },
      }}
    >
      <Box component="span" aria-hidden sx={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: '0 3px 3px 0', bgcolor: AGE[level].bar }} />
      <Box onClick={e => e.stopPropagation()}>
        <Checkbox
          size="small"
          checked={selected}
          onClick={e => onToggle(view.id, e.shiftKey)}
          inputProps={{ 'aria-label': `Select order #${view.orderNo}` }}
          sx={{ p: 0.5, color: C.input, '&.Mui-checked': { color: C.ink } }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AgeBadge view={view} level={level} now={ui.now} />
          <Box component="span" sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: C.text, whiteSpace: 'nowrap', ...tnum }}>#{view.orderNo}</Box>
          <StatusBadge status={view.status} sx={{ ml: 'auto' }} />
        </Box>
        {view.sourceLabel && <Box sx={{ fontSize: 12, color: C.muted }}>{view.typeLabel} · {view.sourceLabel}</Box>}
        {/* On one restaurant's board every card is that restaurant. */}
        {!ui.restaurantId && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <RestaurantLogo restaurant={view.restaurant} size={20} />
            <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {view.restaurant.name}
            </Box>
          </Box>
        )}
        <Box sx={{ minWidth: 0, fontSize: 12, color: C.muted }}>
          {view.customer || view.address ? (
            <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {view.customer?.name}
              {view.customer && ' · '}
              {view.address ? view.address.short : view.typeLabel}
            </Box>
          ) : (
            <NoAddressChip label={view.type === 'PICKUP' ? `${view.typeLabel} · no customer` : 'No address'} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
          <AmountCell view={view} compact />
          <Box onClick={e => e.stopPropagation()} sx={{ minWidth: 0 }}>
            <DeliveryCell view={view} compact />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
